/**
 * On-screen audio debug overlay.
 * Activated by adding ?debug to the URL.
 * Shows every audio event in real time so we can see
 * what fires when the beep happens on Android.
 */

const MAX_LINES = 60;
let el: HTMLDivElement | null = null;
let enabled: boolean | null = null;

function isEnabled(): boolean {
  if (enabled !== null) return enabled;
  enabled = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug');
  return enabled;
}

function ensurePanel(): HTMLDivElement | null {
  if (!isEnabled()) return null;
  if (el) return el;
  el = document.createElement('div');
  el.id = 'audio-debug-overlay';
  el.style.cssText = [
    'position:fixed',
    'bottom:0',
    'left:0',
    'right:0',
    'max-height:35vh',
    'overflow-y:auto',
    'background:rgba(0,0,0,0.9)',
    'color:#0f0',
    'font:11px/1.4 monospace',
    'padding:8px',
    'z-index:99999',
    'pointer-events:auto',
    '-webkit-overflow-scrolling:touch',
  ].join(';');
  document.body.appendChild(el);
  return el;
}

export function audioDebug(tag: string, msg: string) {
  // Always console.log for remote debugging if available
  console.log(`[DBG:${tag}] ${msg}`);

  const panel = ensurePanel();
  if (!panel) return;

  const line = document.createElement('div');
  const ts = new Date().toISOString().slice(11, 23); // HH:mm:ss.SSS
  const color = TAG_COLORS[tag] || '#0f0';
  line.innerHTML = `<span style="color:#888">${ts}</span> <span style="color:${color}">[${tag}]</span> ${escapeHtml(msg)}`;
  panel.appendChild(line);

  // Trim old lines
  while (panel.childNodes.length > MAX_LINES) {
    panel.removeChild(panel.firstChild!);
  }

  panel.scrollTop = panel.scrollHeight;
}

const TAG_COLORS: Record<string, string> = {
  'ctx': '#ff0',       // AudioContext lifecycle
  'prime': '#ff0',     // prime() calls
  'enqueue': '#0ff',   // audio chunks
  'keepalive': '#0f0', // silent oscillator
  'tts': '#f0f',       // tts_start / tts_end
  'state': '#fa0',     // voiceState changes
  'stt': '#f55',       // SpeechRecognition events — RED for visibility
  'stop': '#888',      // stop() calls
  'drain': '#888',     // drain polling
  'chunk': '#066',     // individual chunks (dimmer)
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
